import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { Map } from "maplibre-gl";
import { useEffect, useState, useContext, useRef } from "react";
import { GenerateGeom } from "src/managers/GenerateGeom";
import { MapInteractionState, MapitStateContext, actionSetDrawContext, actionSetMapInteractionState } from "src/states/MapitState";
import tLength from "@turf/length";
import * as Turf from "@turf/turf"
import tArea from "@turf/area";
import { Localization } from "@viamap/viamap2-common";
import { MitCount } from "./ComponentUtils";
import ReactDOM from "react-dom";
import { LayerFunc } from "src/managers/LayerFunc";
import { ProtectedFeature } from "./ProtectedFeature";
import { Feature } from "src/states/ApplicationStateFeatures";
import { RightPaneContext, RightPaneModus, actionGoToModus } from "src/states/RightPaneState";
import { DrawingToolExtras } from "./DrawingToolsExtra";
import { ExtendWithMeasureMents } from "./DrawingToolsMeasure";
import { formatArea, formatLength } from "./DrawingToolFunctions";
import * as MapboxDrawGeodesic from "mapbox-gl-draw-geodesic";
import { GlassButton } from "./GlassButtons";
import { ExtendedMenuButton, SidePanelScrolling } from "./MitGlassComponents";
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassFoldUdBox, TooltipWrapper } from "src/propertyInfoTemplates/PropertyComponents";
import { openWindow, WindowId } from "src/WindowManager/WindowState";
import { windowContext } from "src/WindowManager/windowContext";
import { ActionTracker } from "src/common/managers/ActionTracker";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import {
  SnapPolygonMode,
  SnapPointMode,
  SnapLineMode,
  SnapDirectSelect,
} from "mapbox-gl-draw-snap-mode";
import { Slider } from "./MitInputComponents";

// Not in use


export function DrawingTools(props: { show: boolean }) {
  const { state: mapitState, dispatch: mapitStateDispatch } = useContext(MapitStateContext)
  const mapRef = useRef<Map>()

  useEffect(() => {
    if (mapitState?.map?.getMapPop?.() && props.show) {
      mapRef.current = mapitState.map.getMapPop()
      mapitStateDispatch(actionSetMapInteractionState(MapInteractionState.Override));
      return () => { mapitStateDispatch(actionSetMapInteractionState(MapInteractionState.Normal)) }
    }
  }, [mapitState.map, props.show])

  if (mapitState?.map?.getMapPop?.()) {
    return <DrawingModeSelector map={mapitState.map.getMapPop()} show={props.show} />
  }
  return <></>
}





export function DrawingModeSelector(props: { map, show }) {
  const { dispatch: windowDispatch } = useContext(windowContext);
  const { state: mapitState, dispatch: mapitStateDispatch } = useContext(MapitStateContext);
  const { dispatch: rightPaneDispatch } = useContext(RightPaneContext);
  const [selected, setSelected] = useState<string[]>([]);
  const drawRef = useRef<MapboxDraw>()


  const openRef = useRef<boolean>(false)

  useEffect(() => {
    openRef.current = !!props.show
  }, [props.show])

  function updateState() {
    if (openRef.current && drawRef.current?.getSelectedIds?.()) {
      setSelected([...drawRef.current.getSelectedIds()])
    }
    else {
      setSelected([])
    }
  }

  MapboxDraw.constants.classes.CONTROL_BASE = "maplibregl-ctrl";
  MapboxDraw.constants.classes.CONTROL_PREFIX = "maplibregl-ctrl-";
  MapboxDraw.constants.classes.CONTROL_GROUP = "maplibregl-ctrl-group";

  let StaticMode: any = {};
  StaticMode.onSetup = function () {
    this.setActionableState(); // default actionable state is false for all actions
    return {};
  };
  StaticMode.toDisplayFeatures = function () {
    this.toDisplayFeatures();
  };

  useEffect(() => {
    let modes: any = {
      "static": StaticMode,
      ...MapboxDraw.modes,
      draw_point: SnapPointMode,
      draw_polygon: SnapPolygonMode,
      draw_line_string: SnapLineMode,
      direct_select: SnapDirectSelect,
    };
    modes = ExtendWithMeasureMents.enable(modes);
    modes = MapboxDrawGeodesic.enable(modes);

    const drawObj = new MapboxDraw({
      modes: modes,
      displayControlsDefault: false,
      styles: DrawingToolExtras.styles,
      defaultMode: "static",

      userProperties: true,
      // Config snapping features
      snap: true,
      snapOptions: {
        snapPx: 5,
        snapGetFeatures: (map, draw) => {
          let rex = /^(gl-draw)|(guide)/
          let smIdx = map.getLayersOrder().findIndex((a) => a == "part-2-end");
          return [
            ...map.queryRenderedFeatures({
              layers:
                map.getLayersOrder().filter((layerId: string, idx: number) => {
                  if (rex.test(layerId)) {
                    return false
                  } else {
                    return idx > smIdx
                  }
                }),
            }),
            ...draw.getAll().features
          ]
        }
      }
    });
    drawRef.current = drawObj;
    drawObj.snap = {}
    mapitStateDispatch(actionSetDrawContext(drawObj))
    props.map.addControl(drawObj);


    let x = updateState
    props.map.on('draw.modechange', x)
    props.map.on('draw.update', x)
    props.map.on('draw.create', x)
    props.map.on('draw.selectionchange', x)
    props.map.on('draw.delete', x)

    return () => {
      props.map.off('draw.modechange', x);
      props.map.off('draw.update', x);
      props.map.off('draw.create', x);
      props.map.off('draw.selectionchange', x);
      props.map.off('draw.delete', x);
      try {
        drawObj?.deleteAll?.();
        props.map?.removeControl?.(drawObj);
      } catch (error) {
        console.error(error)
      }
      drawRef.current = undefined;
    }
  }, [props.map])

  useEffect(() => {
    if (props.show) {
      drawRef?.current?.changeMode?.('simple_select')
      let layers = props.map.getStyle().layers;
      let layerIDS = layers.map((layer) => layer.id);
      layerIDS.filter((layerID) => layerID.startsWith("gl-draw")).forEach((glDrawId) => {
        props.map.moveLayer(glDrawId);
      })
      return () => {
        if (props.map.hasControl(drawRef.current)) {
          drawRef?.current?.changeMode?.('static')
          // props.map.removeControl(drawRef.current)
        }
      }
    }
  }, [props.show])

  function handleChangeMode(mode: string | "draw_line_string" | "draw_polygon" | "static" | "draw_circle") {
    if (drawRef.current) {
      drawRef.current.changeMode(mode)
      ActionTracker.track("DrawingTool", "Measurement", "ChangeMode", { mode: mode });
      updateState()
    }
  }

  function handleChangeSnapMode(mode: string | "logEvent" | "logObject" | "logState" | "enabledVertices") {
    if (drawRef.current) {
      drawRef.current.changeSnap(mode, !drawRef.current.snap[mode])
      // ActionTracker.track("DrawingTool", "Measurement", "ChangeSnapMode", { mode: mode, value: drawRef.current.snap[mode] });
    }
  }

  function handleDelete(all: boolean) {
    if (drawRef.current) {
      if (all || selected.length < 1) {
        drawRef.current.deleteAll()
      } else {
        drawRef.current.trash()
      }
    }
    drawRef.current?.changeMode?.('simple_select')
    updateState()
  }

  if (!props.show) {
    return <></>
  }

  function showPointsInArea() {
    const features = drawRef.current?.getSelected().features as any;
    if (features) {
      const polygons = features.filter((a) => { return a.geometry.type === "Polygon" }) as any[]
      const mulipolygon = Turf.multiPolygon(polygons.map((feature) => feature.geometry.coordinates))
      let spatialReturn = GenerateGeom.spatialSelectionGeoJSON(mapitState, mulipolygon.geometry);
      return Object.keys(spatialReturn).reduce((b, a) => { return spatialReturn[a].length + b }, 0)
    }
    return 0
  }




  function showArea() {
    const features = drawRef.current?.getSelected().features;
    let area = 0
    if (features) {
      const polygons = features.filter((a) => { return a.geometry.type === "Polygon" })
      if (polygons.length > 0) {
        area = polygons.reduce((pre, cur) => {
          if (cur.properties.circleRadius) {
            cur = Turf.circle(cur.geometry.coordinates[0][0], cur.properties.circleRadius, { properties: cur.properties })
          }
          return pre + tArea(cur)
        }, area);
      }
    }
    //m2
    return formatArea(area)
  }

  function gemSelection(continueToLayers = true) {
    const ls = drawRef.current?.getSelected();
    if (ls) {
      // let currentDate = new Date();
      ActionTracker.track("DrawingTool", "Measurement", "SaveMeasurement");
      ls.features = ls.features.map((a) => {
        if (a.properties.circleRadius) {
          a = Turf.circle(a.geometry.coordinates[0][0], a.properties.circleRadius, { properties: a.properties })
        }
        let radius = a.properties.circleRadius ? { radius: formatLength(a.properties.circleRadius) } : null
        return { ...a, properties: { "Details": "Measurement", length: formatLength(Turf.length(a as any)), area: formatArea(Turf.area(a as any)), ...radius } }
      })
      let newLayerInfo = LayerFunc.createLayerInfoFromGeoJsonFeatureList(ls.features, Localization.getText("Measurement"))
      windowDispatch(openWindow(WindowId.NameOnCreation, { layers: newLayerInfo }))
      drawRef.current?.delete(drawRef.current?.getSelectedIds?.() || [])
      setSelected([])
      if (continueToLayers) {
        rightPaneDispatch(actionGoToModus(RightPaneModus.Map_Layer))
      }
    }
  }

  function showLength() {
    const features = drawRef.current?.getSelected().features;
    let length = 0
    if (features) {
      if (features.length > 0) {
        length = features.reduce((pre, cur) => {
          if (cur.properties.circleRadius) {
            cur = Turf.circle(cur.geometry.coordinates[0][0], cur.properties.circleRadius, { properties: cur.properties })
          }
          return pre + tLength(cur)
        }, length);
      }
    }
    //Kilometers
    return formatLength(length)
  }



  const modus = drawRef.current?.getMode() || ""

  const areal = showArea()
  const pointCount = showPointsInArea()

  return (<>
    {ReactDOM.createPortal(
      <AdvancedDragModal
        variant={"NSWhite"}
        title={Localization.getText("Draw")}
        PosDefault={{ left: "calc( 100% - 10px)", transform: "translateX(-100%)", top: "10px", width: "400px" }}
      >

        {/* <div className="DrawingArea" style={{pointerEvents:"none"}}> */}
        {/* </div> */}
        {drawRef.current?.getAll().features.length && selected.length ?
          <>
            <ADModalBody>
              {selected.length > 1 ? <h5>{Localization.getText("Selection")} {selected.length}</h5> : null}
              {areal !== "0m2" ? <div><b>{Localization.getText("M_Area")} </b>{showArea()}</div> : null}
              <div><b>{Localization.getText("Length")} </b>{showLength()}</div>
              {pointCount > 0 ? <ProtectedFeature feature={Feature.MeasurementToolSpatial} ><div><b>{Localization.getText("Points")} </b> <MitCount show={true} count={showPointsInArea()} /></div></ProtectedFeature> : <></>}
            </ADModalBody>
            <ADModalFooter>
              {selected.length ? <GlassButton onClick={() => gemSelection()}>{Localization.getText("Gem")}</GlassButton> : <></>}
            </ADModalFooter>
          </>
          : <>
            <ADModalBody>
              <ol>
                <li>{Localization.getText("Select a tool")}</li>
                <li>{Localization.getText("Click the map")}</li>
                <li>{Localization.getText("Click the last point to finish the geometry")}</li>
              </ol>
              <GlassFoldUdBox
                foldetUd={false}
                title={"Tips"}
                variant="Small"
              >
                <ul>
                  <li>{Localization.getText("Selected geometries are yellow")}</li>
                  <li>{Localization.getText("Selected points are bigger")}</li>
                  <li>{Localization.getText("Shift + click to select more")}</li>
                  <li>{Localization.getText("Move selected geomtries by dragging the border")}</li>
                  <li>{Localization.getText("Move selected points by dragging them")}</li>
                  <li>{Localization.getText("Remove selected geometries or points by clicking trash")}</li>
                  <li>{Localization.getText("Clicking trash deletes all geometries when nothing is selected")}</li>
                  <li>{Localization.getText("Add new points to a geomtri by clicking the green points, that are show when clicking the border of selected geometri")}</li>
                </ul>
              </GlassFoldUdBox>
            </ADModalBody>
          </>
        }
      </AdvancedDragModal>
      , document.getElementById("Mit-MapOverlay") || document.body)}

    <div className="ExtendedSideBar">
      <div className='LeftInnerShadow' ></div>
      <SidePanelScrolling>
        <ExtendedMenuButton className={"buttonOverride ExtendedMenuButton " + (modus === "draw_line_string" ? "active" : "")} toolTip={Localization.getText("Line")} onClick={() => handleChangeMode("draw_line_string")}>
          <MIBIcon icon="DrawLineSegment" />
        </ExtendedMenuButton>
        <ExtendedMenuButton className={"buttonOverride ExtendedMenuButton " + (modus === "draw_polygon" ? "active" : "")} toolTip={Localization.getText("Polygon")} onClick={() => handleChangeMode("draw_polygon")}>
          <MIBIcon icon="DrawPolygon" />
        </ExtendedMenuButton>
        <ProtectedFeature feature={Feature.DrawCircle} contentsIfNoAccess={<></>} >
          <ExtendedMenuButton className={"buttonOverride ExtendedMenuButton " + (modus === "draw_circle" ? "active" : "")} toolTip={Localization.getText("Circle")} onClick={() => handleChangeMode("draw_circle")} >
            <MIBIcon icon="DrawCircle" />
          </ExtendedMenuButton>
        </ProtectedFeature>
        <ExtendedMenuButton className={"buttonOverride ExtendedMenuButton "} toolTip={Localization.getText("Trash")} onClick={() => handleDelete(false)} onDoubleClick={() => handleDelete(true)}>
          <MIBIcon icon="DrawTrash" />
        </ExtendedMenuButton>
      </SidePanelScrolling>
    </div>
    {(modus === "draw_polygon" || modus === "draw_line_string") ?


      <div className='ExtendedSideBar DrawSnap' >
        <div className="DrawSnapping">
          <TooltipWrapper title={Localization.getText("Snap (Data or Vector layer)")} >
            <button title="Snap" className={"button " + (drawRef.current.options.snap ? "active" : "")}
              onClick={() => {
                drawRef.current.options.snap = !drawRef.current.options.snap
                updateState()
              }}
            >
              <MIBIcon icon="snap" />
            </button>
          </TooltipWrapper>
          {
            drawRef.current.options.snap ?
              <>
                <Slider min={1} max={20} value={drawRef.current.options.snapOptions?.snapPx || 5} onChange={(val) => {
                  if (drawRef.current) {
                    drawRef.current.options.snapOptions.snapPx = val
                    updateState()
                  }
                }} />
                <span className="snapDist" title="snap distance">{(drawRef.current.options.snapOptions?.snapPx)?.toLocaleString()}px</span>
              </>
              : null
          }
        </div>
      </div >
      : null}
  </>
  )
}